import { Component, OnDestroy, AfterViewInit, Renderer2, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { MessagesModule } from 'primeng/messages';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MenuService } from './app.menu.service';
import { AppSidebarComponent } from './app.sidebar.component';
import { AppTopbarComponent } from './app.topbar.component';
import { AppBreadcrumbComponent } from './app.breadcrumb.component';
import { AppProfileSidebarComponent } from './app.profilesidebar.component';
import { LayoutService } from './service/app.layout.service';
import { InMemoryCacheService } from '../common';
import { Location } from '@angular/common';

import { InputTextModule } from 'primeng/inputtext';
import { TranslateModule } from '@ngx-translate/core';


@Component({
    selector: 'app-layout',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        MessagesModule,
        ToastModule,
        ProgressSpinnerModule,
        AppTopbarComponent,
        AppBreadcrumbComponent,
        AppProfileSidebarComponent,
        InputTextModule,
        TranslateModule
    ],
    templateUrl: './app.layout.component.html',
    styles: `

        @media screen and (min-width: 1960px) {
            .layout-content, .landing-wrapper {
                margin-left: var(--layoutContentMargin) !important;
                margin-right: var(--layoutContentMargin) !important;
            }
        }

        @media screen and (min-width: 992px) {
           .layout-content-wrapper {
                margin-left: var(--layoutContentWrapperMarginLeft) !important;
            }
        }

        :host ::ng-deep {
            .custom-spinner {
                .p-progress-spinner-circle {
                    animation: custom-progress-spinner-color 6s ease-in-out infinite;
                }
            }

            .layout-container, .layout-container > .layout-content-wrapper, .layout-container .layout-content {
                min-height: 100vh;
                height: 100%;
                display: flex;
                flex-direction: column;
            }

            .layout-container > .layout-content-wrapper { overflow: visible; }
            .layout-container .layout-content { flex: 1 1 auto; overflow: visible; }
            .layout-container .layout-content .p-table { overflow: visible; }
            .p-paginator { overflow: visible; }
            body > .p-dropdown-panel { z-index: 1200; }

            @keyframes custom-progress-spinner-color {
                100%,
                0% {
                    stroke: #000000;
                }
                33% {
                    stroke: #873EFE;
                }
                66% {
                    stroke: #FFF624;
                }
            }

        }
`
})
export class AppLayoutComponent implements OnDestroy, AfterViewInit {

    overlayMenuOpenSubscription: Subscription;

    menuOutsideClickListener: any;

    menuScrollListener: any;

    @ViewChild(AppSidebarComponent) appSidebar!: AppSidebarComponent;

    @ViewChild(AppTopbarComponent) appTopbar!: AppTopbarComponent;

    layoutContentMargin = 'auto';
    layoutContentWrapperMarginLeft = '8rem';
    hideMainMenu = false;

    constructor(private menuService: MenuService, public layoutService: LayoutService, public renderer: Renderer2, public router: Router,
        public cache: InMemoryCacheService, private location: Location) {

        if (this.isAccountFlow()) {
            this.layoutContentMargin = '0';
            this.layoutContentWrapperMarginLeft = '0';
            this.hideMainMenu = true;
        }

        this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
            if (!this.menuOutsideClickListener) {
                this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
                    const isOutsideClicked = !(
                        this.appTopbar.el.nativeElement.isSameNode(event.target) ||
                        this.appTopbar.el.nativeElement.contains(event.target) ||
                        this.appTopbar.menuButton.nativeElement.isSameNode(event.target) ||
                        this.appTopbar.menuButton.nativeElement.contains(event.target)
                    );
                    if (isOutsideClicked) {
                        this.hideMenu();
                    }
                });
            }
            if ((this.layoutService.isHorizontal() || this.layoutService.isSlim()|| this.layoutService.isSlimPlus()) && !this.menuScrollListener) {
                this.menuScrollListener = this.renderer.listen(this.appTopbar.appSidebar.menuContainer.nativeElement, 'scroll', event => {
                    if (this.layoutService.isDesktop()) {
                        this.hideMenu();
                    }
                });
            }
            if (this.layoutService.state.staticMenuMobileActive) {
                this.blockBodyScroll();
            }
        });

        this.router.events.pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                this.hideMenu();
            });
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        }
        else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        }
        else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' +
                'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

    hideMenu() {
        this.layoutService.state.overlayMenuActive = false;
        this.layoutService.state.staticMenuMobileActive = false;
        this.layoutService.state.menuHoverActive = false;
        this.layoutService.state.sidebarActive = false;
        this.menuService.reset();
        if(this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }
        if (this.menuScrollListener) {
            this.menuScrollListener();
            this.menuScrollListener = null;
        }
        this.unblockBodyScroll();
    }

    get containerClass() {
        return {
            'layout-light': this.layoutService.config().colorScheme === 'light',
            'layout-dark': this.layoutService.config().colorScheme === 'dark',
            'layout-overlay': this.layoutService.config().menuMode === 'overlay',
            'layout-static': this.layoutService.config().menuMode === 'static',
            'layout-slim': this.layoutService.config().menuMode === 'slim',
            'layout-slim-plus': this.layoutService.config().menuMode === 'slim-plus',
            'layout-horizontal': this.layoutService.config().menuMode === 'horizontal',
            'layout-reveal': this.layoutService.config().menuMode === 'reveal',
            'layout-drawer': this.layoutService.config().menuMode === 'drawer',
            'layout-static-inactive': this.layoutService.state.staticMenuDesktopInactive && this.layoutService.config().menuMode === 'static',
            'layout-overlay-active': this.layoutService.state.overlayMenuActive,
            'layout-mobile-active': this.layoutService.state.staticMenuMobileActive,
            'p-ripple-disabled': !this.layoutService.config().ripple,
            'layout-sidebar-active': this.layoutService.state.sidebarActive,
            'layout-sidebar-anchored': this.layoutService.state.anchored
        }
    }

    ngOnDestroy() {
        if (this.overlayMenuOpenSubscription) {
            this.overlayMenuOpenSubscription.unsubscribe();
        }

        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }
    }

    ngAfterViewInit() {
        // Fix ExpressionChangedAfterItHasBeenCheckedError by triggering change detection after view init
        setTimeout(() => {
            this.hideMainMenu = this.isAccountFlow();
        });
    }

    onLogoutClicked() {
        this.cache.logout();
    }

    isAccountFlow() {
        return this.location.path().split('?')[0].includes('account');
    }

    get avatarLabel() {
        return this.cache.user?.firstName?.charAt(0).toUpperCase();
    }

}
